import { g } from "../../util/index.ts";
import {
	NUM_ACTIVE_PITCHERS,
	NUM_STARTING_PITCHERS,
} from "../../../common/constants.baseball.ts";

// Shared roster planning for cuts, free agency and minor-league promotions.
// Every slot has a distinct player: a versatile shortstop cannot also count as
// the backup catcher, and spare starters compete for relief roles by RP rating.
export type RosterPlayer = {
	pid?: number;
	born?: { year: number };
	ratings: {
		pos: string;
		ovr: number;
		pot: number;
		cat?: number;
		ovrs?: Record<string, number>;
	}[];
	injury?: { gamesRemaining: number };
};
export const fieldPositions = ["C", "1B", "2B", "3B", "SS", "LF", "CF", "RF"];

// Bench and depth roles that accept more than one position
const ROLE_GROUPS: Record<string, string[]> = {
	IF: ["2B", "SS", "3B", "1B"],
	OF: ["CF", "LF", "RF"],
	UT: [...fieldPositions, "DH"],
};
type Slot = { pos: string; weight: number };

type RoleRatings = {
	pos: string;
	ovrs?: Record<string, number>;
	cat?: number;
};

export const pitchingRating = (r: RoleRatings) =>
	Math.max(r.ovrs?.SP ?? 0, r.ovrs?.RP ?? 0);

const fieldingRating = (r: RoleRatings) =>
	Math.max(0, ...ROLE_GROUPS.UT!.map((pos) => defensiveRoleScore(r, pos)));

// A real pitcher, or a two-way player whose arm is as good as his glove and bat.
// Position players with a token RP rating never count, so they don't fill the bullpen.
export const isPitcherCapable = (r: RoleRatings) =>
	r.pos === "SP" ||
	r.pos === "RP" ||
	pitchingRating(r) >= Math.max(40, fieldingRating(r) - 5);

// Rectangular Hungarian assignment. Dummy columns represent unfilled roles.
const solveAssignment = (scores: number[][]): number[] => {
	const n = scores.length;
	if (!n) return [];
	const realColumns = scores[0]!.length;
	const m = Math.max(n, realColumns);
	// Dense costs avoid optional array lookups in the innermost assignment loop.
	// Preserve the column order and the original floating-point operations/tie breaks.
	const stride = m + 1;
	const costs = new Float64Array((n + 1) * stride);
	for (let i = 1; i <= n; i++) {
		const row = scores[i - 1]!;
		for (let j = 1; j <= m; j++) costs[i * stride + j] = -(row[j - 1] ?? 0);
	}
	const u = new Float64Array(n + 1),
		v = new Float64Array(m + 1);
	const p = new Int32Array(m + 1),
		way = new Int32Array(m + 1);
	const min = new Float64Array(m + 1);
	const used = new Uint8Array(m + 1);
	for (let i = 1; i <= n; i++) {
		p[0] = i;
		let j0 = 0;
		min.fill(Infinity);
		used.fill(0);
		do {
			used[j0] = 1;
			const i0 = p[j0]!;
			const offset = i0 * stride;
			const rowPotential = u[i0]!;
			let delta = Infinity,
				j1 = 0;
			for (let j = 1; j <= m; j++) {
				if (used[j]) continue;
				const cost = costs[offset + j]! - rowPotential - v[j]!;
				if (cost < min[j]!) {
					min[j] = cost;
					way[j] = j0;
				}
				if (min[j]! < delta) {
					delta = min[j]!;
					j1 = j;
				}
			}
			for (let j = 0; j <= m; j++) {
				if (used[j]) {
					u[p[j]!]! += delta;
					v[j]! -= delta;
				} else min[j]! -= delta;
			}
			j0 = j1;
		} while (p[j0] !== 0);
		do {
			const j1 = way[j0]!;
			p[j0] = p[j1]!;
			j0 = j1;
		} while (j0 !== 0);
	}
	const result = Array<number>(n).fill(-1);
	for (let j = 1; j <= m; j++)
		if (p[j] && j <= realColumns) result[p[j]! - 1] = j - 1;
	return result;
};

// The AI plans the same rosters with the same scores again and again: every day for each club, for each
// minor leaguer it considers calling up and for each trade or signing it looks at. The answer depends only on
// the scores, so it is remembered by a 64-bit hash of them and reused exactly. Bounded so it can't grow forever.
const ASSIGNMENT_CACHE_SIZE = 20000;
const assignmentCache = new Map<string, number[]>();
const hashView = new DataView(new ArrayBuffer(8));
const hashValues = (values: number[], seed1: number, seed2: number) => {
	let h1 = seed1;
	let h2 = seed2;
	h1 = Math.imul(h1 ^ values.length, 0x01000193);
	h2 = Math.imul(h2 ^ values.length, 0x85ebca6b);
	for (const value of values) {
		hashView.setFloat64(0, value);
		const lo = hashView.getUint32(0);
		const hi = hashView.getUint32(4);
		h1 = Math.imul(Math.imul(h1 ^ lo, 0x01000193) ^ hi, 0x01000193);
		h2 = Math.imul(Math.imul(h2 ^ hi, 0x85ebca6b) ^ lo, 0xc2b2ae35);
		h2 ^= h2 >>> 13;
	}
	return [h1 >>> 0, h2 >>> 0] as const;
};
const scoresKey = (scores: number[][]) => {
	let h: readonly [number, number] = [0x811c9dc5, 0x9e3779b9];
	for (const row of scores) h = hashValues(row, h[0], h[1]);
	return `${scores.length}:${h[0].toString(36)}:${h[1].toString(36)}`;
};
const cachedAssignment = (key: string, scores: number[][]) => {
	let result = assignmentCache.get(key);
	if (!result) {
		result = solveAssignment(scores);
		if (assignmentCache.size >= ASSIGNMENT_CACHE_SIZE) assignmentCache.clear();
		assignmentCache.set(key, result);
	}
	// Callers may change the array they get
	return result.slice();
};
export const assignRoles = (scores: number[][]): number[] => {
	if (!scores.length) return [];
	return cachedAssignment(scoresKey(scores), scores);
};

// Weights follow how much each role plays: the rotation and lineup every day, the
// closer and setup men most nights, the back of the bullpen and the bench rarely.
const SP_WEIGHTS = [1, 1, 0.95, 0.9, 0.85];
const RP_WEIGHTS = [0.8, 0.75, 0.7, 0.65, 0.6, 0.5, 0.45, 0.4, 0.35, 0.3];
// One backup per field position, most important first so a smaller limit drops the least needed
const BACKUP_ROLES: Slot[] = [
	{ pos: "C", weight: 0.4 },
	{ pos: "SS", weight: 0.3 },
	{ pos: "CF", weight: 0.3 },
	{ pos: "2B", weight: 0.28 },
	{ pos: "3B", weight: 0.26 },
	{ pos: "RF", weight: 0.24 },
	{ pos: "LF", weight: 0.22 },
	{ pos: "1B", weight: 0.2 },
];
const FLEX_WEIGHT = 0.15;

export const pitcherSlots = (): Slot[] => [
	...SP_WEIGHTS.slice(0, NUM_STARTING_PITCHERS).map((weight) => ({
		pos: "SP",
		weight,
	})),
	...RP_WEIGHTS.slice(0, NUM_ACTIVE_PITCHERS - NUM_STARTING_PITCHERS).map(
		(weight) => ({ pos: "RP", weight }),
	),
];

// The Gui's composition for every CPU club: 15 pitchers (5 SP, 10 RP, which is exactly
// how many the game dresses), a starter and a backup at each field position, and one
// more player, the DH when the league uses it. That closes 32. A smaller limit drops
// the extra, then the least needed backups, then the back of the bullpen; a bigger one
// adds flexible depth slots that any player can take.
export const rosterSlots = (size: number, dh = true): Slot[] => {
	size = Math.max(0, Math.floor(size));
	const rotation = pitcherSlots().slice(0, NUM_STARTING_PITCHERS);
	const relief = pitcherSlots().slice(NUM_STARTING_PITCHERS);
	const starters: Slot[] = fieldPositions.map((pos) => ({ pos, weight: 1 }));
	const extra: Slot = dh
		? { pos: "DH", weight: 1 }
		: { pos: "UT", weight: 0.3 };

	const slots: Slot[] = [
		...rotation.slice(0, Math.min(rotation.length, size)),
		...starters.slice(0, Math.max(0, size - rotation.length)),
	];
	let remaining = Math.max(0, size - slots.length);
	const full = relief.length + BACKUP_ROLES.length + 1;
	if (remaining >= full) {
		slots.push(...relief, ...BACKUP_ROLES, extra);
	} else {
		// Shrink bullpen and bench together, keeping the backup catcher longest
		const numRelief = Math.min(
			relief.length,
			Math.round((remaining * relief.length) / (full - 1)),
		);
		slots.push(...relief.slice(0, numRelief));
		remaining -= numRelief;
		slots.push(...BACKUP_ROLES.slice(0, remaining));
		remaining -= Math.min(remaining, BACKUP_ROLES.length);
		if (remaining > 0) {
			slots.push(extra);
		}
	}
	while (slots.length < size) {
		slots.push({ pos: "ANY", weight: FLEX_WEIGHT });
	}
	return slots;
};

// In the first seasons of a league, real players stay at their real positions: the label is not changed
// (see setPos) and their own position is worth this much more than any other, so the AI only moves them
// for emergency coverage. After that the AI is free to move players around by ratings.
export const REAL_POSITION_SEASONS = 5;
const REAL_POSITION_BONUS = 15;
export const realPositionsProtected = () =>
	// Unit tests may plan rosters without a league loaded
	Object.hasOwn(g, "startingSeason") &&
	Object.hasOwn(g, "season") &&
	g.get("season") < g.get("startingSeason") + REAL_POSITION_SEASONS;

export const defensiveRoleScore = (
	r: { ovrs?: Record<string, number>; cat?: number; pos?: string },
	pos: string,
	fallback = 0,
) => {
	let raw = r.ovrs?.[pos] ?? fallback;
	if (
		r.pos === pos &&
		pos !== "SP" &&
		pos !== "RP" &&
		pos !== "DH" &&
		Number.isFinite(raw) &&
		realPositionsProtected()
	) {
		raw += REAL_POSITION_BONUS;
	}
	// Overall includes batting: a great bat with 6 catching must not displace
	// an actual catcher. Still permit emergency coverage if nobody is qualified.
	const penalty =
		pos === "C" && r.cat !== undefined ? Math.max(0, 40 - r.cat) * 1.5 : 0;
	return Number.isFinite(raw) ? Math.max(0, raw - penalty) : 0;
};
export const roleRating = (p: RosterPlayer, pos: string): number => {
	const r = p.ratings.at(-1)!;
	if (pos === "SP" || pos === "RP") {
		// Position players don't pitch while real pitchers are available
		return isPitcherCapable(r)
			? defensiveRoleScore(r, pos, r.pos === pos ? r.ovr : 0)
			: 0;
	}
	if (pos === "ANY") {
		return Math.max(
			roleRating(p, "SP") * 0.9,
			roleRating(p, "RP"),
			roleRating(p, "UT"),
		);
	}
	const group = ROLE_GROUPS[pos];
	if (group) {
		return Math.max(0, ...group.map((pos2) => roleRating(p, pos2)));
	}
	return defensiveRoleScore(r, pos, r.pos === pos ? r.ovr : 0);
};
// Age rules for the big club. Up to 18 a player stays in the minors. At 19 he can be called up
// only for a regular role (lineup, rotation or the top of the bullpen) earned by current
// ability, never for potential. From 20 on he is judged like anyone else.
export const MAX_MINORS_ONLY_AGE = 18;
export const EXCEPTIONAL_CALLUP_AGE = 19;
export const REGULAR_ROLE_WEIGHT = 0.6;
const playerAge = (p: { born?: { year: number } }) =>
	p.born ? g.get("season") - p.born.year : Infinity;
export const canFillSlot = (p: { born?: { year: number } }, weight: number) => {
	const age = playerAge(p);
	if (age <= MAX_MINORS_ONLY_AGE) {
		return false;
	}
	if (age === EXCEPTIONAL_CALLUP_AGE) {
		return weight >= REGULAR_ROLE_WEIGHT;
	}
	return true;
};
export const isBigClubEligible = (p: { born?: { year: number } }) =>
	playerAge(p) > MAX_MINORS_ONLY_AGE;

// Compute each distinct role once per player/batch, even when the roster has
// five SP slots, ten RP slots, backups and repeated depth roles.
export const scoreRosterSlots = (p: RosterPlayer, slots: Slot[]) => {
	const ratings = new Map<string, number>();
	const r = p.ratings.at(-1)!;
	const age = playerAge(p);
	return slots.map((slot) => {
		if (
			age <= MAX_MINORS_ONLY_AGE ||
			(age === EXCEPTIONAL_CALLUP_AGE && slot.weight < REGULAR_ROLE_WEIGHT)
		)
			return 0;
		let rating = ratings.get(slot.pos);
		if (rating === undefined) {
			rating = roleRating(p, slot.pos);
			ratings.set(slot.pos, rating);
		}
		const health =
			(p.injury?.gamesRemaining ?? 0) > 0
				? slot.weight === 1
					? 0.65
					: 0.9
				: 1;
		const growth =
			slot.pos === "ANY"
				? Math.min(15, Math.max(0, r.pot - r.ovr)) *
					Math.min(1, rating / Math.max(1, r.ovr)) *
					0.15
				: 0;
		return slot.weight * rating * health + growth;
	});
};
// Scope this cache to a single unchanged candidate pool and roster configuration.
// In autoSign it is discarded before the next simulated day.
export const createRosterScoreCache = (size: number, dh = true) => {
	const slots = rosterSlots(size, dh);
	const cache = new WeakMap<RosterPlayer, number[]>();
	return (p: RosterPlayer) => {
		let scores = cache.get(p);
		if (!scores) {
			scores = scoreRosterSlots(p, slots);
			cache.set(p, scores);
		}
		return scores;
	};
};

// The scores key of each plan, so adding one candidate only hashes his column
const planKeys = new WeakMap<object, string>();
export const planRoster = <T extends RosterPlayer>(
	players: T[],
	size: number,
	dh = true,
) => {
	const slots = rosterSlots(size, dh);
	const columns = players.map((p) => scoreRosterSlots(p, slots));
	const scores = slots.map((_, i) => columns.map((column) => column[i]!));
	const key = scores.length ? scoresKey(scores) : "";
	const assignment = scores.length ? cachedAssignment(key, scores) : [];
	const selected = new Set(
		assignment.filter((i) => i >= 0).map((i) => players[i]!),
	);
	const total = assignment.reduce(
		(sum, index, i) => sum + (scores[i]![index] ?? 0),
		0,
	);
	const plan = { slots, scores, assignment, selected, total };
	planKeys.set(plan, key);
	return plan;
};

// Reuse only within an unchanged roster evaluation: injuries, ratings, age and transactions are read again
// on the next call. The assignment itself is cached by the exact scores (the baseline's key plus the
// candidate's column), so the same plan on another day costs nothing.
export const planRosterAddition = <T extends RosterPlayer>(
	roster: T[],
	candidate: T,
	baseline: ReturnType<typeof planRoster<T>>,
) => {
	const column = scoreRosterSlots(candidate, baseline.slots);
	const scores = baseline.scores.map((row, i) => [...row, column[i]!]);
	const [c1, c2] = hashValues(column, 0x27d4eb2f, 0x165667b1);
	const assignment = scores.length
		? cachedAssignment(
				`${planKeys.get(baseline) ?? scoresKey(baseline.scores)}+${c1.toString(36)}:${c2.toString(36)}`,
				scores,
			)
		: [];
	const players = [...roster, candidate];
	const selected = new Set(
		assignment.filter((i) => i >= 0).map((i) => players[i]!),
	);
	const total = assignment.reduce(
		(sum, index, i) => sum + (scores[i]![index] ?? 0),
		0,
	);
	return { slots: baseline.slots, scores, assignment, selected, total };
};

export const rosterImprovement = <T extends RosterPlayer>(
	roster: T[],
	candidate: T,
	size: number,
	dh = true,
	baseline = planRoster(roster, size, dh),
) => {
	if (size <= 0) return 0;
	const next = planRosterAddition(roster, candidate, baseline);
	return next.selected.has(candidate)
		? Math.max(0, next.total - baseline.total)
		: 0;
};

// minGainFull is how much a candidate must add to a full roster. Free agents cost money, so
// they need a clear gain; a club's own minor leaguer goes up as soon as he beats anyone in
// any role, starter or backup, with the rest of the roster shifting positions to fit.
export const rankRosterCandidates = <T extends RosterPlayer>(
	roster: T[],
	candidates: T[],
	size: number,
	dh = true,
	minGainFull = 3,
	scoreCandidate?: (p: RosterPlayer) => number[],
) => {
	const baseline = planRoster(roster, size, dh);
	// Cheap upper estimate first; exact assignment only for the best 24. This
	// keeps large imported leagues usable on phones without rating every FA/team
	// combination with a full assignment search.
	const shortlist = candidates
		.map((p, order) => ({
			p,
			order,
			estimate: Math.max(
				0,
				...(scoreCandidate
					? scoreCandidate(p)
					: scoreRosterSlots(p, baseline.slots)
				).map(
					(score, i) =>
						score - (baseline.scores[i]![baseline.assignment[i]!] ?? 0),
				),
			),
		}))
		.filter((x) => x.estimate > 0)
		.sort((a, b) => b.estimate - a.estimate || a.order - b.order)
		.slice(0, 24);
	return shortlist
		.map((x) => ({
			...x,
			gain: rosterImprovement(roster, x.p, size, dh, baseline),
		}))
		.filter((x) => x.gain >= (roster.length < size ? 0.1 : minGainFull))
		.sort((a, b) => b.gain - a.gain || a.order - b.order)
		.map((x) => x.p);
};
